import {
  format,
  isSameYear,
  fromUnixTime,
  formatDistanceToNow,
} from 'date-fns';
import { enUS } from 'date-fns/locale';

let currentDateFnsLocale = enUS;

export const setDateFnsLocale = locale => {
  if (locale) currentDateFnsLocale = locale;
};

/**
 * Formats a Unix timestamp into a human-readable time format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='h:mm a'] - Desired format of the time.
 * @returns {string} Formatted time string.
 */
export const messageStamp = (time, dateFormat = 'H:mm') => {
  const unixTime = fromUnixTime(time);
  return format(unixTime, dateFormat);
};

/**
 * Provides a formatted timestamp, adjusting the format based on the current year.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const messageTimestamp = (time, dateFormat = 'd.M.') => {
  const messageTime = fromUnixTime(time);
  const now = new Date();
  const messageDate = format(messageTime, dateFormat);
  if (!isSameYear(messageTime, now)) {
    return format(messageTime, 'd.M.yyyy');
  }
  return messageDate;
};

/**
 * Converts a Unix timestamp to a relative time string (e.g., 3 hours ago).
 * @param {number} time - Unix timestamp.
 * @returns {string} Relative time string.
 */
export const dynamicTime = time => {
  if (!time) return null;
  const unixTime = fromUnixTime(time);
  return formatDistanceToNow(unixTime, {
    addSuffix: true,
    locale: currentDateFnsLocale,
  });
};

/**
 * Formats a Unix timestamp into a specified date format.
 * @param {number} time - Unix timestamp.
 * @param {string} [pattern='d.M.yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const dateFormat = (time, pattern = 'd.M.yyyy') => {
  const unixTime = fromUnixTime(time);
  return format(unixTime, pattern);
};

/**
 * Formats a Unix timestamp as a date instead of "3 months ago":
 * today 'H:mm', earlier this year 'd.M. H:mm', older years 'd.M.yyyy'.
 * @param {number} time - Unix timestamp.
 * @returns {string}
 */
export const relativeTime = time => {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const startOfYear = new Date();
  startOfYear.setMonth(0, 1);
  startOfYear.setHours(0, 0, 0, 0);
  if (time * 1000 > startOfToday.getTime()) {
    return dateFormat(time, 'H:mm');
  }
  if (time * 1000 > startOfYear.getTime()) {
    return dateFormat(time, 'd.M. H:mm');
  }
  return dateFormat(time, 'd.M.yyyy');
};

/**
 * Converts a detailed time description into a shorter format, optionally appending 'ago'.
 * @param {string} time - Detailed time description (e.g., 'a minute ago').
 * @param {boolean} [withAgo=false] - Whether to append 'ago' to the result.
 * @returns {string} Shortened time description.
 */
export const shortTimestamp = (time, withAgo = false) => {
  // This function takes a time string and converts it to a short time string
  // with the following format: 1m, 1h, 1d, 1mo, 1y
  // The function also takes an optional boolean parameter withAgo
  // which will add the word "ago" to the end of the time string
  const suffix = withAgo ? ' ago' : '';
  const timeMappings = {
    'less than a minute ago': 'now',
    'a minute ago': `1m${suffix}`,
    'an hour ago': `1h${suffix}`,
    'a day ago': `1d${suffix}`,
    'a month ago': `1mo${suffix}`,
    'a year ago': `1y${suffix}`,
  };
  // Check if the time string is one of the specific cases
  if (timeMappings[time]) {
    return timeMappings[time];
  }
  const convertToShortTime = time
    .replace(/about|over|almost|/g, '')
    .replace(' minute ago', `m${suffix}`)
    .replace(' minutes ago', `m${suffix}`)
    .replace(' hour ago', `h${suffix}`)
    .replace(' hours ago', `h${suffix}`)
    .replace(' day ago', `d${suffix}`)
    .replace(' days ago', `d${suffix}`)
    .replace(' month ago', `mo${suffix}`)
    .replace(' months ago', `mo${suffix}`)
    .replace(' year ago', `y${suffix}`)
    .replace(' years ago', `y${suffix}`);
  return convertToShortTime;
};

/**
 * Formats a stored date string in the agent's time zone and dashboard language, with the zone
 * label, e.g. "Nov 27, 2025, 9:05 PM GMT+5:30". Returns the raw value when it cannot be parsed.
 * @param {string} value - Any string Date can parse (a visitor's Date.toString(), an ISO time)
 * @param {string} locale - Dashboard locale, e.g. "cs" or "pt_BR"
 * @param {string} [timeZone] - IANA zone; the browser's own zone when omitted
 * @returns {string}
 */
export const formatInAgentTimeZone = (value, locale, timeZone) => {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const options = {
    dateStyle: 'medium',
    timeStyle: 'short',
    ...(timeZone ? { timeZone } : {}),
  };
  const dateTime = new Intl.DateTimeFormat(
    locale?.replace('_', '-'),
    options
  ).format(date);
  const zoneName = new Intl.DateTimeFormat(locale?.replace('_', '-'), {
    timeZoneName: 'short',
    ...(timeZone ? { timeZone } : {}),
  })
    .formatToParts(date)
    .find(part => part.type === 'timeZoneName')?.value;

  return zoneName ? `${dateTime} (${zoneName})` : dateTime;
};
